"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import {
  streamChat,
  resumeStream,
} from "@/app/lib/sse";
import { fetchSession, addRecord, patchRecord, deleteRecord, startBackgroundJob, pollBackgroundJob, cancelChatStream } from "@/app/lib/api";
import type {
  ChatRecord,
  StreamingResponse,
  SSEEvent,
  ProviderRequest,
  ChatRequest,
  SessionRecord,
  JudgeResponse,
  ModeInfo,
} from "@/app/lib/types";
import { isFeatureEnabled } from "@/app/lib/clientConfig";
import { modeUsesBackground } from "@/app/lib/modes";
import { transitionStreamPresentation } from "@/app/lib/streamState";

export interface UseChatReturn {
  chatHistory: ChatRecord[];
  activeResponses: Record<string, StreamingResponse>;
  activeProviderOrder: string[];
  isStreaming: boolean;
  isLoadingHistory: boolean;
  sendMessage: (
    prompt: string,
    providers: ProviderRequest[],
    mode: string,
    sessionId: string,
    conversationHistory?: Array<{ role: string; content: string }>,
    attachmentIds?: string[],
    folderContext?: string,
    folderContextVersionId?: string,
    customQueryPrompt?: string,
    attachmentNames?: string[],
    attachmentIdsList?: string[],
    isTemporary?: boolean,
    autoJudgeRequested?: boolean
  ) => Promise<void>;
  stopStreaming: () => void;
  // Set by finalize when the stream was user-stopped AND the partial record
  // saved cleanly. The composer reads this to swap Send → Discard for one
  // interaction, giving the user a quick exit if the partial wasn't worth
  // keeping. Cleared by: starting a new stream, switching sessions, calling
  // discardRecentlyStopped, or clearRecentlyStopped (e.g. on first keystroke).
  recentlyStoppedRecord: { sessionId: string; recordId: string } | null;
  // Synchronously set when the user presses Stop, before finalize/addRecord
  // completes. Used to render Discard IMMEDIATELY so the user sees the
  // affordance while the partial record is still being saved server-side
  // (~500-2000ms of network wait on mobile). Without this flag the composer
  // falls back to Send during the gap, which looks like the Discard feature
  // is broken. Cleared by the same triggers as recentlyStoppedRecord.
  stopJustPressed: boolean;
  discardRecentlyStopped: () => Promise<void>;
  clearRecentlyStopped: () => void;
  loadHistory: (sessionId: string) => Promise<void>;
  clearChat: () => void;
  // Per-provider retry for terminal-error cards (OQ-S6-a). Attempts to
  // resume from the server-side stream buffer / background job store first;
  // falls back to a fresh single-provider POST /chat/stream. On success,
  // patches the record so the recovered output persists across reloads.
  retryProvider: (
    sessionId: string,
    recordIndex: number,
    providerRequest: ProviderRequest,
    conversationHistory?: Array<{ role: string; content: string }>
  ) => Promise<void>;
  // Update a record's judge verdict in-place without a server round-trip.
  // Used by /judge SSE consumer to render scoring/synthesis incrementally.
  // Pass null to clear (e.g., on fatal judge error).
  setRecordJudgeVerdict: (recordIndex: number, verdict: JudgeResponse | null) => void;
  // Soft-delete helpers for the long-press / right-click → Delete query
  // flow. removeRecord pulls the record out of chatHistory by server id
  // and returns it (along with its prior index) so the caller can
  // schedule a 5s undo + deferred server DELETE. restoreRecord puts it
  // back at the original index if the user taps Undo.
  removeRecord: (recordId: string) => { record: ChatRecord; index: number } | null;
  restoreRecord: (record: ChatRecord, index: number) => void;
  // Always-fresh ref to chatHistory. State-based reads can be stale within
  // a single async function (await loadHistory queues a setState; the next
  // line still sees the old captured value). The ref is updated
  // SYNCHRONOUSLY whenever loadHistory or sendMessage mutates the history,
  // so callers awaiting these can read the new value immediately.
  chatHistoryRef: React.MutableRefObject<ChatRecord[]>;
  // Client-minted record id for the IN-FLIGHT stream (if any), so the
  // active per-card play button can render with a valid recordId before
  // the record is archived. Null when no stream is in flight or when
  // the current session is temp (no server record_id).
  activeRecordId: string | null;
}

function buildChatRecordFromSessionRecord(record: SessionRecord): ChatRecord {
  const responses: Record<string, StreamingResponse> = {};
  const providerOrder = record.targets || [];

  for (const [providerId, result] of Object.entries(record.providers)) {
    const meta = result.metadata as { background_job_id?: string; stream_id?: string } | undefined;
    responses[providerId] = {
      id: providerId,
      text: result.output || "",
      isComplete: true,
      error: result.error,
      model: result.model,
      mode: result.mode,
      durationMs: result.duration_ms,
      usage: result.usage,
      meta: result.meta,
      wasInterrupted: false,
      options: result.options,
      metadata: result.metadata,
      ...(meta?.background_job_id ? { backgroundJobId: meta.background_job_id } : {}),
      ...(meta?.stream_id ? { streamId: meta.stream_id } : {}),
    };
  }

  return {
    prompt: record.prompt,
    mode: record.mode,
    responses,
    providerOrder,
    timestamp: new Date(record.created_at),
    serverRecordId: record.id,
    ...(record.attachment_names ? { attachmentNames: record.attachment_names } : {}),
    ...(record.attachment_ids ? { attachmentIds: record.attachment_ids } : {}),
    ...(record.judge_verdict ? { judgeVerdict: record.judge_verdict } : {}),
    ...(record.judge_started_at ? { judgeStartedAt: record.judge_started_at } : {}),
    ...(record.auto_judge_requested ? { autoJudgeRequested: true } : {}),
    ...(record.created_by ? { createdBy: record.created_by } : {}),
  };
}

// Per-session streaming state. Each in-flight chat owns one of these,
// keyed by session id. Replaces the previous single-stream global refs
// so concurrent sends across different sessions can't comingle text or
// stomp each other's abort controllers (root cause of the 2026-05-04
// fin-mkts-leaking-into-replit cross-contamination bug). iOS canonical:
// each session has its own ChatViewModel; this Map is the equivalent
// for a single-hook web architecture.
interface StreamingBucket {
  responses: Record<string, StreamingResponse>;
  // Ephemeral UI state, intentionally separate from `responses` so it can
  // never be archived into a record, session, or LocalCache.
  narrationStatuses: Record<string, string>;
  providerOrder: string[];
  prompt: string | null;
  isStreaming: boolean;
  abortController: AbortController | null;
  pollTimer: ReturnType<typeof setInterval> | null;
  // Server-side stream id (from the first stream_id SSE event). Used by
  // stopStreaming to call POST /chat/stream/{id}/cancel and actually halt
  // provider tasks instead of just closing the local fetch. Without this,
  // Stop is purely cosmetic and tokens keep burning.
  streamId: string | null;
  // Set true when the user pressed Stop. Lets the finalize block know to
  // capture the saved record id into recentlyStoppedRecord so the Discard
  // button can replace Send for one interaction.
  userStopped: boolean;
  // Client-minted record id sent in the ChatRequest so the server can
  // persist the completed result into sessions_{user}.json itself (Item A
  // of design/web_parity_pending.md — durable chat persistence via
  // record_id). Reused on the finalize addRecord call so the server's
  // upgrade-on-conflict merge sees the same id and no duplicate record is
  // created. Null on temp chats (server-side persist is skipped without
  // a session_id anyway). Mirrors iOS ChatViewModel.activeRetryRecordId.
  recordId: string | null;
}

function withNarrationStatuses(
  responses: Record<string, StreamingResponse>,
  narrationStatuses: Record<string, string>
): Record<string, StreamingResponse> {
  const presented: Record<string, StreamingResponse> = {};
  for (const [provider, response] of Object.entries(responses)) {
    const narrationStatus = narrationStatuses[provider];
    presented[provider] = narrationStatus
      ? { ...response, narrationStatus }
      : response;
  }
  return presented;
}

/** Rebuild buffered provider state with the same vocabulary as live SSE. */
function replayBufferedEvents(
  events: readonly SSEEvent[],
  initialResponses: Record<string, StreamingResponse>
): {
  responses: Record<string, StreamingResponse>;
  narrationStatuses: Record<string, string>;
  unrecognizedEventTypes: string[];
} {
  const responses = { ...initialResponses };
  const narrationStatuses: Record<string, string> = {};
  const unrecognizedEventTypes: string[] = [];

  for (const event of events) {
    switch (event.type) {
      case "stream_id":
      case "keepalive":
        // Connection-level events do not alter provider state during replay.
        break;
      case "model_used": {
        const current = responses[event.provider];
        if (current) responses[event.provider] = { ...current, model: event.model };
        break;
      }
      case "delta": {
        const current = responses[event.provider];
        if (current) {
          responses[event.provider] = { ...current, text: current.text + event.delta };
          delete narrationStatuses[event.provider];
        }
        break;
      }
      case "narration_boundary": {
        const current = responses[event.provider];
        if (current) {
          narrationStatuses[event.provider] = current.text;
          responses[event.provider] = { ...current, text: "" };
        }
        break;
      }
      case "citations_replace": {
        const current = responses[event.provider];
        if (current) {
          responses[event.provider] = { ...current, text: event.text };
          delete narrationStatuses[event.provider];
        }
        break;
      }
      case "done": {
        const current = responses[event.provider];
        if (current) {
          responses[event.provider] = {
            ...current, isComplete: true, model: event.model,
            durationMs: event.duration_ms, options: event.options,
            usage: event.usage, meta: event.meta,
          };
          delete narrationStatuses[event.provider];
        }
        break;
      }
      case "error": {
        const current = responses[event.provider];
        if (current) {
          // Deliberately retain streamId/backgroundJobId: an error does not
          // prove the server buffer will not later contain a full response.
          responses[event.provider] = { ...current, isComplete: true, error: event.error };
          delete narrationStatuses[event.provider];
        }
        break;
      }
      default: {
        const exhaustiveCheck: never = event;
        const unknown = exhaustiveCheck as { type: string };
        console.error("Unhandled stream-buffer event type", unknown.type);
        unrecognizedEventTypes.push(unknown.type);
      }
    }
  }
  return { responses, narrationStatuses, unrecognizedEventTypes };
}

export function useChat(
  sessionId: string | null,
  onSessionSwitch?: (restorePrompt: string | null) => void,
  // Fired when a queued Discard finishes deleting the record AND that
  // record was the only one in its session. Caller deletes the session.
  // Sync-path Discard (snap available at tap time) handles its own
  // session-delete in page.tsx onDiscard; this covers the pending path
  // (Discard tapped before recordId arrived → drain watcher fires the
  // record-delete here later).
  onDiscardEmptiedSession?: (sid: string) => void,
  modes: readonly ModeInfo[] = []
): UseChatReturn {
  const [chatHistory, setChatHistory] = useState<ChatRecord[]>([]);
  const [activeResponses, setActiveResponses] = useState<
    Record<string, StreamingResponse>
  >({});
  const [activeProviderOrder, setActiveProviderOrder] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // Exposed so page.tsx can pass it as `recordId` to the ACTIVE
  // streaming render — lets per-card play buttons fire before the
  // record archives (Tests 1.2 + 1.11). Mirrors bucket.recordId for
  // the currently-streaming session.
  const [activeRecordId, setActiveRecordId] = useState<string | null>(null);
  const currentSessionRef = useRef<string | null>(null);
  // Mirror chatHistory to a ref so async callbacks (retryProvider) can read
  // the current value without threading it through as a dep.
  const chatHistoryRef = useRef<ChatRecord[]>([]);
  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  // After Stop saves a partial record, we briefly offer a Discard button
  // in the Send slot so the user can drop the record without having to
  // open the per-record menu. Cleared by:
  //   • the user typing in the composer (handled in page.tsx via setter)
  //   • starting a new stream (cleared at top of sendMessage)
  //   • switching sessions (cleared in loadHistory)
  //   • clicking Discard (cleared after deleteRecord succeeds)
  const [recentlyStoppedRecord, setRecentlyStoppedRecord] = useState<
    { sessionId: string; recordId: string } | null
  >(null);

  // Mirrors recentlyStoppedRecord but flips synchronously on Stop press,
  // before the async finalize/addRecord round-trip completes. Closes the
  // visible-Discard gap on slow mobile networks. See type doc for details.
  const [stopJustPressed, setStopJustPressed] = useState(false);

  // Per-session in-flight streaming state. Lives only while a stream is
  // active for that session; cleaned up after addRecord+loadHistory finish.
  const bucketsRef = useRef<Map<string, StreamingBucket>>(new Map());

  const getOrCreateBucket = useCallback((sid: string): StreamingBucket => {
    let b = bucketsRef.current.get(sid);
    if (!b) {
      b = {
        responses: {},
        narrationStatuses: {},
        providerOrder: [],
        prompt: null,
        isStreaming: false,
        abortController: null,
        pollTimer: null,
        streamId: null,
        userStopped: false,
        recordId: null,
      };
      bucketsRef.current.set(sid, b);
    }
    return b;
  }, []);

  const cleanupBucket = useCallback((sid: string) => {
    const b = bucketsRef.current.get(sid);
    if (b?.pollTimer) clearInterval(b.pollTimer);
    bucketsRef.current.delete(sid);
  }, []);

  const clearChat = useCallback(() => {
    chatHistoryRef.current = [];
    setChatHistory([]);
    setActiveResponses({});
    setActiveProviderOrder([]);
    setIsStreaming(false);
  }, []);

  // Reconcile pending/stuck provider entries on a freshly loaded session
  // (T7, decision 14, INV-6). Pulls final content from the server-side job
  // store for providers that were mid-flight when the previous session
  // visit was interrupted.
  //
  // Failure-mode policy:
  //   - Server `status: "done"`  → patch the record with recovered output.
  //   - Server `status: "error"` → treat as unrecoverable; if ≥30 s have
  //     elapsed since record creation, flip provider to error (retry
  //     affordance is shown by ResponseCard).
  //   - HTTP 404 (job/stream expired) → same as `status: "error"`.
  //   - HTTP 5xx / network error / `status: "running"` → transient; leave
  //     the pending state in place so the user sees "pending" (INV-6).
  //   - Within 30 s of creation → always transient, even on 404, to buffer
  //     against server-restart windows (INV-12, decision 14).
  const TERMINAL_ERROR_GRACE_MS = 30_000;
  const reconcileStuckRecords = useCallback(async (sid: string, records: ChatRecord[]) => {
    type Target = {
      recordIndex: number;
      serverRecordId: string;
      providerId: string;
      jobId: string;
      createdAt: number;
    };
    const targets: Target[] = [];
    records.forEach((record, idx) => {
      if (!record.serverRecordId) return;
      const createdAt = record.timestamp instanceof Date
        ? record.timestamp.getTime()
        : Date.now();
      for (const [providerId, resp] of Object.entries(record.responses)) {
        if (resp.backgroundJobId && !resp.text) {
          targets.push({
            recordIndex: idx,
            serverRecordId: record.serverRecordId,
            providerId,
            jobId: resp.backgroundJobId,
            createdAt,
          });
        }
      }
    });
    if (targets.length === 0) return;

    // Helper: flip one provider entry to terminal-error locally AND persist
    // the flip via PATCH so subsequent session loads keep the error state.
    const flipToError = async (target: Target, errorMsg: string) => {
      let priorModel = "";
      setChatHistory((prev) => {
        if (target.recordIndex >= prev.length) return prev;
        const rec = prev[target.recordIndex];
        const current = rec.responses[target.providerId];
        // Respect INV-3: if output arrived somehow between check and
        // update, don't overwrite it with an error.
        if (!current || current.text) return prev;
        priorModel = current.model || "";
        const updated = [...prev];
        updated[target.recordIndex] = {
          ...rec,
          responses: {
            ...rec.responses,
            [target.providerId]: {
              ...current,
              isComplete: true,
              error: errorMsg,
              backgroundJobId: undefined,
            },
          },
        };
        return updated;
      });
      try {
        await patchRecord(sid, target.serverRecordId, {
          providers: {
            [target.providerId]: {
              model: priorModel,
              error: errorMsg,
              // Clear the metadata handle so future loads don't retry
              // forever. Empty dict replaces the prior metadata on write.
              metadata: {},
            },
          },
        });
      } catch (patchErr) {
        console.error("reconcile: PATCH (terminal-error) failed", patchErr);
      }
    };

    for (const target of targets) {
      if (currentSessionRef.current !== sid) return;
      const ageMs = Date.now() - target.createdAt;
      try {
        const status = await pollBackgroundJob(target.jobId);

        // Happy path: job completed, we got results — patch the record.
        if (status.status === "done" && status.results) {
          const match = status.results.find((r) => r.provider === target.providerId);
          if (!match || !match.output) continue;

          setChatHistory((prev) => {
            if (target.recordIndex >= prev.length) return prev;
            const rec = prev[target.recordIndex];
            const current = rec.responses[target.providerId];
            if (!current || current.text) return prev;
            const updated = [...prev];
            updated[target.recordIndex] = {
              ...rec,
              responses: {
                ...rec.responses,
                [target.providerId]: {
                  ...current,
                  text: match.output || "",
                  model: match.model || current.model,
                  durationMs: match.duration_ms ?? current.durationMs,
                  usage: match.usage,
                  meta: match.meta,
                  error: undefined,
                  backgroundJobId: undefined,
                },
              },
            };
            return updated;
          });

          try {
            await patchRecord(sid, target.serverRecordId, {
              providers: {
                [target.providerId]: {
                  model: match.model || "",
                  output: match.output,
                  duration_ms: match.duration_ms,
                  options: match.options,
                  usage: match.usage,
                },
              },
            });
          } catch (patchErr) {
            console.error("reconcile: PATCH failed", patchErr);
          }
          continue;
        }

        // Definitive error from server — flip after the 30s grace.
        if (status.status === "error") {
          if (ageMs >= TERMINAL_ERROR_GRACE_MS) {
            await flipToError(
              target,
              status.error || "Background job failed"
            );
          }
          continue;
        }

        // Still running — leave pending. The next reconcile pass (on next
        // session activate) will try again.
        // status === "running" falls through here with no state change.
      } catch (err) {
        // Parse error for HTTP 404 — apiFetch wraps status into the message
        // as "Request failed (404)" for non-2xx responses. 404 on
        // /chat/background/{id} means the job record is gone: expired or
        // never existed. Treat as definitive unrecoverable once past grace.
        const msg = err instanceof Error ? err.message : String(err);
        const is404 = msg.includes("(404)");
        if (is404 && ageMs >= TERMINAL_ERROR_GRACE_MS) {
          await flipToError(target, "This response could not be recovered. Try again.");
          continue;
        }
        // Transient: network error, 5xx, 404-within-grace. Leave pending.
        console.error("reconcile: poll failed (transient)", err);
      }
    }
  }, []);

  const loadHistory = useCallback(async (sid: string) => {
    setIsLoadingHistory(true);
    let lastErr: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const session = await fetchSession(sid);
        const records = (session.records || []).map(buildChatRecordFromSessionRecord);
        // Synchronous ref update so callers awaiting loadHistory can read the
        // fresh history immediately, BEFORE React commits the state update.
        chatHistoryRef.current = records;
        setChatHistory(records);
        setIsLoadingHistory(false);
        reconcileStuckRecords(sid, records);
        return;
      } catch (err) {
        lastErr = err;
        console.error(`Failed to load chat history (attempt ${attempt + 1}):`, err);
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }
    }
    setIsLoadingHistory(false);
    console.error("loadHistory failed after 2 attempts:", lastErr);
  }, [reconcileStuckRecords]);

  // Clear chat and load history when session changes (unified to prevent race).
  // Streams in OTHER sessions keep running in the background — they own their
  // own bucket, abortController, and pollTimer, so leaving the session does
  // not abort or interfere with them. They finalize to addRecord against
  // their own session id whenever they complete.
  useEffect(() => {
    if (sessionId !== currentSessionRef.current) {
      const prevSessionId = currentSessionRef.current;
      currentSessionRef.current = sessionId;
      // Switching sessions invalidates the post-stop Discard arm — it was
      // tied to a specific record in the previous session.
      setRecentlyStoppedRecord(null);
      setStopJustPressed(false);

      // If we're returning to a session that still has an in-flight bucket,
      // restore its UI state. Otherwise clear and load history.
      const bucket = sessionId ? bucketsRef.current.get(sessionId) : null;
      if (sessionId && bucket && bucket.isStreaming &&
          Object.keys(bucket.responses).length > 0) {
        setActiveResponses(withNarrationStatuses(bucket.responses, bucket.narrationStatuses));
        setActiveProviderOrder([...bucket.providerOrder]);
        setIsStreaming(true);
        onSessionSwitch?.(bucket.prompt);
        loadHistory(sessionId);
      } else {
        chatHistoryRef.current = [];
        setChatHistory([]);
        setActiveResponses({});
        setActiveProviderOrder([]);
        setIsStreaming(false);
        // Only fire the "switched-away" pending-prompt clear when the user
        // was previously viewing another session. Transitioning from a null
        // sessionId to a fresh sid happens MID-handleSendMessage (the caller
        // creates the session, calls setActiveSession, then a few lines
        // later sets pendingPrompt). Firing onSessionSwitch(null) here would
        // race-clobber that pending prompt — leaving the prompt-box missing
        // for the entire stream duration. Verified via diag banner: pp=NULL
        // throughout streaming on brand-new chats.
        if (prevSessionId) {
          onSessionSwitch?.(null);
        }
        if (sessionId) {
          loadHistory(sessionId);
        }
      }
    }
  }, [sessionId, loadHistory, onSessionSwitch]);

  // Stops the stream owned by the currently-viewed session ONLY. Streams
  // in other sessions (started before navigating here) continue running
  // and will finalize to their own session records on completion. The
  // partial output streamed before stop is saved as a record with
  // wasInterrupted markers per provider; finalize captures the saved
  // record id into recentlyStoppedRecord so the composer can offer a
  // one-shot Discard button in the Send slot.
  const stopStreaming = useCallback(() => {
    const sid = currentSessionRef.current;
    if (!sid) {
      setIsStreaming(false);
      return;
    }
    const bucket = bucketsRef.current.get(sid);
    if (!bucket) {
      setIsStreaming(false);
      return;
    }
    // Server-side cancel BEFORE local abort so the server sees a deliberate
    // cancel (task.cancel() on each provider task) rather than a generic
    // client disconnect. The discard flag is now always false from this
    // path — the post-stop Discard button calls deleteRecord directly.
    if (bucket.streamId) {
      cancelChatStream(bucket.streamId, false).catch(() => {
        // Best-effort. Local abort below still tears down the SSE consumer.
      });
    }
    bucket.abortController?.abort();
    bucket.abortController = null;
    if (bucket.pollTimer) {
      clearInterval(bucket.pollTimer);
      bucket.pollTimer = null;
    }
    bucket.isStreaming = false;
    bucket.userStopped = true;
    for (const k of Object.keys(bucket.responses)) {
      if (!bucket.responses[k].isComplete) {
        bucket.responses[k] = { ...bucket.responses[k], wasInterrupted: true, isComplete: true };
      }
    }
    // Arm the Discard affordance synchronously — the actual recordId lands
    // later via setRecentlyStoppedRecord in finalize, but the composer
    // needs Discard visible RIGHT NOW so the user gets immediate feedback.
    setStopJustPressed(true);
    setIsStreaming(false);
    setActiveResponses((prev) => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        if (!updated[key].isComplete) {
          updated[key] = { ...updated[key], wasInterrupted: true, isComplete: true };
        }
      }
      return updated;
    });
  }, []);

  // Drop the record that was just saved by the most-recent Stop. Called
  // from the Discard button that briefly replaces Send in the composer.
  // Idempotent — if recentlyStoppedRecord is already cleared, no-ops.
  // Queued-discard: if the user clicks Discard BEFORE finalize/addRecord
  // lands `recentlyStoppedRecord` (the recordId from the server), we can't
  // delete anything yet — the record doesn't exist server-side. Park the
  // intent here so the watcher useEffect below auto-fires the delete the
  // moment the recordId arrives. Without this, an early Discard click was a
  // silent no-op: the partial-stopped record persisted in chat history and
  // the screen never cleared.
  const pendingDiscardRef = useRef(false);

  const discardRecentlyStopped = useCallback(async () => {
    setStopJustPressed(false);
    const target = recentlyStoppedRecord;
    if (!target) {
      // Recordid not yet landed. Queue — watcher will fire delete when it does.
      pendingDiscardRef.current = true;
      return;
    }
    pendingDiscardRef.current = false;
    setRecentlyStoppedRecord(null);
    try {
      await deleteRecord(target.sessionId, target.recordId);
    } catch (err) {
      console.error("Failed to delete record:", err);
      // Surface to user via reload so they see the record stuck if delete failed
    }
    // Reload history to reflect the deletion (or the failure-restored state).
    if (currentSessionRef.current === target.sessionId) {
      await loadHistory(target.sessionId);
    }
  }, [recentlyStoppedRecord, loadHistory]);

  // Drain the queued Discard intent the moment recentlyStoppedRecord lands.
  // Covers the (common on slow mobile) case: Stop → Discard tapped before the
  // ~500-2000ms addRecord round-trip completes → recordId arrives → we
  // auto-fire the delete here so the user's intent isn't dropped.
  useEffect(() => {
    if (!recentlyStoppedRecord || !pendingDiscardRef.current) return;
    pendingDiscardRef.current = false;
    const target = recentlyStoppedRecord;
    setRecentlyStoppedRecord(null);
    (async () => {
      // Server cascade tells us whether the record-delete also emptied
      // the session. Reading server truth instead of predicting from
      // chatHistoryRef (which handleNewChat may have cleared by now)
      // sidesteps the whole class of "snapshot before clear" bugs the
      // queued discard path has tripped on.
      let sessionDeleted = false;
      try {
        const result = await deleteRecord(target.sessionId, target.recordId);
        sessionDeleted = result.session_deleted;
      } catch (err) {
        console.error("Failed to delete record (queued):", err);
      }
      if (sessionDeleted && onDiscardEmptiedSession) {
        // Server already deleted the session; client mirror via the
        // hook-level deleteSession path (tombstones + local filter).
        onDiscardEmptiedSession(target.sessionId);
      } else if (currentSessionRef.current === target.sessionId) {
        await loadHistory(target.sessionId);
      }
    })();
  }, [recentlyStoppedRecord, loadHistory, onDiscardEmptiedSession]);

  const sendMessage = useCallback(
    async (
      prompt: string,
      providers: ProviderRequest[],
      mode: string,
      sid: string,
      conversationHistory?: Array<{ role: string; content: string }>,
      attachmentIds?: string[],
      folderContext?: string,
      folderContextVersionId?: string,
      customQueryPrompt?: string,
      attachmentNames?: string[],
      attachmentIdsList?: string[],
      isTemporary?: boolean,
      autoJudgeRequested?: boolean
    ): Promise<void> => {
      // Pre-set the session ref so the "clear on session change" effect
      // doesn't wipe our active responses when the parent sets activeSessionId
      // right before calling sendMessage.
      currentSessionRef.current = sid;

      // Capture initiation time (not completion time) for display
      const initiatedAt = new Date();

      // Initialize active responses
      const providerIds = providers.map((p) => p.provider);

      const initialResponses: Record<string, StreamingResponse> = {};
      for (const p of providers) {
        initialResponses[p.provider] = {
          id: p.provider,
          text: "",
          isComplete: false,
          wasInterrupted: false,
          model: p.model,
          mode: p.mode,
          options: p.options as Record<string, unknown> | undefined,
        };
      }
      // Allocate the per-session bucket. All SSE / poll callbacks below
      // close over `bucket` directly — they never touch a shared ref —
      // so concurrent sends in different sessions don't comingle.
      // Starting a new stream invalidates any post-stop Discard arm — the
      // user has moved on, so the Send button should reappear immediately.
      setRecentlyStoppedRecord(null);
      setStopJustPressed(false);

      const bucket = getOrCreateBucket(sid);
      bucket.responses = { ...initialResponses };
      bucket.narrationStatuses = {};
      bucket.providerOrder = providerIds;
      bucket.prompt = prompt;
      bucket.isStreaming = true;
      bucket.abortController = null;
      bucket.pollTimer = null;
      bucket.streamId = null;
      bucket.userStopped = false;
      // Mint a session record id up front for non-temp persisted chats so
      // the server's /chat/stream + /chat/background runners can persist
      // the completed result into sessions_{user}.json themselves — a
      // tab close / network loss between all-providers-done and the
      // client's archive POST no longer orphans the answer (server's
      // append_record is idempotent + upgrade-on-conflict on a later
      // client archive). Temp chats (sid === "") never persist server-
      // side; skip the mint. crypto.randomUUID is available in every
      // browser we ship to (Safari 15.4+, Chrome 92+, Firefox 95+).
      bucket.recordId = sid && !isTemporary ? crypto.randomUUID() : null;
      // Surface the minted id so page.tsx can wire it into the
      // active-streaming card render (per-card play buttons during
      // streaming). Null on temp chats.
      setActiveRecordId(bucket.recordId);
      setActiveProviderOrder(providerIds);
      setActiveResponses(initialResponses);
      setIsStreaming(true);

      // Yield one frame so React renders placeholder cards before streaming begins
      await new Promise((r) => requestAnimationFrame(r));

      // Attach conversation history and custom query prompt to each provider request
      const providersWithHistory = providers.map((p) => ({
        ...p,
        conversation_history: conversationHistory || [],
        ...(customQueryPrompt ? { options: { ...p.options, custom_query_prompt: customQueryPrompt } } : {}),
      }));

      const chatRequest: ChatRequest = {
        prompt,
        providers: providersWithHistory,
        mode,
        // Opaque, per-request correlation id for provider trace events.
        // It is deliberately random and independent of chat content or identity.
        trace_id: crypto.randomUUID(),
        session_id: sid,
        attachment_ids: attachmentIds,
        folder_context: folderContext,
        folder_context_version_id: folderContextVersionId,
        // Server flags this in admin_debug log entries so the user's
        // opted-in export can distinguish temp from persisted chats.
        ...(isTemporary ? { is_temporary: true } : {}),
        // Server persists the completed result under this id (see
        // bucket.recordId comment). Reused on addRecord below; merge
        // is idempotent on the server's upgrade-on-conflict path.
        ...(bucket.recordId ? { record_id: bucket.recordId } : {}),
        // Server-owned auto-judge (v1.6, bug #7): hand the verdict to the
        // server so it lands even if the tab closes / sleeps before the
        // client fires /judge. Server skips it for temp (no record_id).
        ...(autoJudgeRequested ? { auto_judge_requested: true } : {}),
      };

      // Helper: check if user is still viewing this session
      const isStillActive = () => currentSessionRef.current === sid;

      if (!modeUsesBackground(mode, modes)) {
        // SSE streaming
        const controller = new AbortController();
        bucket.abortController = controller;

        try {
          await streamChat(
            chatRequest,
            (event: SSEEvent) => {
              switch (event.type) {
                case "stream_id":
                  // Update ref
                  {
                    // Stamp the bucket itself so stop/discard can find the
                    // server-side stream id without poking through provider
                    // entries. Also stamp the first provider entry for
                    // back-compat with the resume-from-buffer code paths.
                    bucket.streamId = event.stream_id;
                    const fk = Object.keys(bucket.responses)[0];
                    if (fk) {
                      bucket.responses[fk] = {
                        ...bucket.responses[fk],
                        streamId: event.stream_id,
                      };
                    }
                  }
                  // Update React state only if still viewing this session
                  if (isStillActive()) {
                    setActiveResponses((prev) => {
                      const updated = { ...prev };
                      const firstKey = Object.keys(updated)[0];
                      if (firstKey) {
                        updated[firstKey] = {
                          ...updated[firstKey],
                          streamId: event.stream_id,
                        };
                      }
                      return updated;
                    });
                  }
                  break;

                case "model_used":
                  // Server-side model swap (e.g. Anthropic Fast Haiku→Opus
                  // for stock-chart prompts) — patch response.model so the
                  // metadata bar shows the model that's actually running.
                  {
                    const cur = bucket.responses[event.provider];
                    if (cur) {
                      bucket.responses[event.provider] = {
                        ...cur,
                        model: event.model,
                      };
                    }
                  }
                  if (isStillActive()) {
                    setActiveResponses((prev) => ({
                      ...prev,
                      [event.provider]: {
                        ...prev[event.provider],
                        model: event.model,
                      },
                    }));
                  }
                  break;

                case "delta":
                  // Always accumulate in ref
                  {
                    const cur = bucket.responses[event.provider];
                    if (cur) {
                      bucket.responses[event.provider] = {
                        ...cur,
                        text: (cur.text || "") + event.delta,
                      };
                      delete bucket.narrationStatuses[event.provider];
                    }
                  }
                  // Update React state only if still viewing this session
                  if (isStillActive()) {
                    setActiveResponses((prev) => transitionStreamPresentation(prev, event));
                  }
                  break;

                case "narration_boundary":
                  {
                    const cur = bucket.responses[event.provider];
                    if (cur) {
                      bucket.narrationStatuses[event.provider] = cur.text || "";
                      bucket.responses[event.provider] = { ...cur, text: "" };
                    }
                  }
                  if (isStillActive()) {
                    setActiveResponses((prev) => transitionStreamPresentation(prev, event));
                  }
                  break;

                case "citations_replace":
                  {
                    const cur = bucket.responses[event.provider];
                    if (cur) {
                      bucket.responses[event.provider] = {
                        ...cur,
                        text: event.text,
                      };
                      delete bucket.narrationStatuses[event.provider];
                    }
                  }
                  if (isStillActive()) {
                    setActiveResponses((prev) => transitionStreamPresentation(prev, event));
                  }
                  break;

                case "done":
                  // Terminal events can arrive immediately after a narration
                  // boundary, before an answer delta. Keep this ephemeral and
                  // provider-keyed; it is never copied into persisted responses.
                  delete bucket.narrationStatuses[event.provider];
                  {
                    const cur = bucket.responses[event.provider];
                    if (cur) {
                      bucket.responses[event.provider] = {
                        ...cur,
                        isComplete: true,
                        model: event.model,
                        durationMs: event.duration_ms,
                        options: event.options,
                        usage: event.usage,
                        meta: event.meta,
                      };
                    }
                  }
                  if (isStillActive()) {
                    setActiveResponses((prev) => {
                      const presented = transitionStreamPresentation(prev, event);
                      return {
                        ...presented,
                      [event.provider]: {
                        ...presented[event.provider],
                        isComplete: true,
                        model: event.model,
                        durationMs: event.duration_ms,
                        options: event.options,
                        usage: event.usage,
                        meta: event.meta,
                      },
                      };
                    });
                  }
                  break;

                case "error":
                  // See done: clear even when no answer text ever arrived.
                  delete bucket.narrationStatuses[event.provider];
                  {
                    const cur = bucket.responses[event.provider];
                    if (cur) {
                      bucket.responses[event.provider] = {
                        ...cur,
                        isComplete: true,
                        error: event.error,
                      };
                    }
                  }
                  if (isStillActive()) {
                    setActiveResponses((prev) => {
                      const presented = transitionStreamPresentation(prev, event);
                      return {
                        ...presented,
                      [event.provider]: {
                        ...presented[event.provider],
                        isComplete: true,
                        error: event.error,
                      },
                      };
                    });
                  }
                  break;

                case "keepalive":
                  break;
              }
            },
            controller.signal
          );
        } catch (err) {
          // Distinguish user-stop from watchdog idle timeout. Both are
          // AbortError, but the watchdog tags its abort with message
          // "stream-idle" so we can fall through to the resume + force-
          // complete path instead of silently bailing. iOS canonical: same
          // pattern via URLSession's timeoutIntervalForRequest.
          const isAbort = err instanceof DOMException && err.name === "AbortError";
          const isUserStop = isAbort && err.message === "user-stop";
          if (isUserStop) {
            // User cancelled via stop button — don't resume.
          } else {
            if (isAbort) {
              console.warn("Stream idle timeout — forcing finalize:", err);
            } else {
              console.error("Stream error:", err);
            }

            // Try to resume from server-side stream buffer
            let resumed = false;
            const fk = Object.keys(bucket.responses)[0];
            const streamIdToResume = fk ? bucket.responses[fk]?.streamId : undefined;

            if (isFeatureEnabled("stream_auto_resume") && streamIdToResume) {
              try {
                const result = await resumeStream(streamIdToResume);
                // Resume endpoint returns { status, events, stream_id }.
                // If buffer is still streaming, poll until complete or timeout.
                interface ResumeResponse { status: string; events: SSEEvent[]; stream_id: string }
                let resumeData = result as unknown as ResumeResponse | null;

                if (resumeData && resumeData.status === "streaming") {
                  // Poll up to 10 times (30s total) for the buffer to complete
                  for (let poll = 0; poll < 10 && resumeData?.status === "streaming"; poll++) {
                    await new Promise((r) => setTimeout(r, 3000));
                    resumeData = (await resumeStream(streamIdToResume)) as unknown as ResumeResponse | null;
                  }
                }

                if (resumeData && resumeData.events && resumeData.events.length > 0) {
                  // Reset ref text before replaying — the buffer has ALL events
                  // from the start, so replaying on top of partial data would
                  // double-count deltas received before the connection dropped.
                  for (const key of Object.keys(bucket.responses)) {
                    bucket.responses[key] = {
                      ...bucket.responses[key],
                      text: "",
                      isComplete: false,
                      error: undefined,
                      durationMs: undefined,
                    };
                  }
                  const replay = replayBufferedEvents(resumeData.events, bucket.responses);
                  bucket.responses = replay.responses;
                  bucket.narrationStatuses = replay.narrationStatuses;
                  if (isStillActive()) {
                    setActiveResponses({ ...bucket.responses });
                  }
                  resumed = true;
                }
              } catch {
                // Buffer not available — fall through to error marking
              }
            }

            if (!resumed) {
              for (const key of Object.keys(bucket.responses)) {
                if (!bucket.responses[key].isComplete) {
                  bucket.responses[key] = {
                    ...bucket.responses[key],
                    text: bucket.responses[key].text || "",
                    isComplete: true,
                    wasInterrupted: true,
                  };
                }
              }
              if (isStillActive()) {
                setActiveResponses({ ...bucket.responses });
              }
            }
          }
        }

        bucket.abortController = null;
      } else {
        // Background job for pro/deep_research
        try {
          const { job_id } = await startBackgroundJob(chatRequest);

          // Stash the job_id on every provider so that if the user walks
          // away before the job finishes, the saved record carries a
          // recovery handle for reconcileStuckRecords to pull from.
          for (const key of Object.keys(bucket.responses)) {
            bucket.responses[key] = {
              ...bucket.responses[key],
              backgroundJobId: job_id,
            };
          }

          // Poll for results
          await new Promise<void>((resolve, reject) => {
            const poll = async () => {
              try {
                const status = await pollBackgroundJob(job_id);

                // Show partial results as each provider completes
                if (status.results && status.results.length > 0) {
                  const partialResults = status.results;
                  for (const result of partialResults) {
                    if (!bucket.responses[result.provider]?.isComplete) {
                      bucket.responses[result.provider] = {
                        id: result.provider,
                        text: result.output || "",
                        isComplete: true,
                        error: result.error,
                        model: result.model,
                        durationMs: result.duration_ms,
                        wasInterrupted: false,
                        options: result.options,
                        usage: result.usage,
                        meta: result.meta,
                      };
                    }
                  }
                  if (isStillActive()) {
                    setActiveResponses({ ...bucket.responses });
                  }
                }

                if (status.status === "done") {
                  resolve();
                } else if (status.status === "error") {
                  for (const key of Object.keys(bucket.responses)) {
                    bucket.responses[key] = {
                      ...bucket.responses[key],
                      isComplete: true,
                      error: status.error || "Background job failed",
                    };
                  }
                  if (isStillActive()) {
                    setActiveResponses({ ...bucket.responses });
                  }
                  resolve();
                }
                // If still "running", continue polling
              } catch (err) {
                reject(err);
              }
            };

            bucket.pollTimer = setInterval(poll, 3000);
            // Do an immediate first poll
            poll();
          });

          if (bucket.pollTimer) {
            clearInterval(bucket.pollTimer);
            bucket.pollTimer = null;
          }
        } catch (err) {
          // collapse to `bundle.js:2:<offset>`, which is useless. Capturing
          // name+message+stack into the saved error field lets us read the
          // real throw site from the server-side admin_debug log.
          const e = err as (Error & { fileName?: string; lineNumber?: number; columnNumber?: number });
          const diag = `${e?.name || "Error"}: ${e?.message || String(err)}`
            + (e?.fileName ? ` @ ${e.fileName}:${e?.lineNumber}:${e?.columnNumber}` : "")
            + (e?.stack ? `\n${e.stack}` : "");
          console.error("Background job error:", err, "stack:", e?.stack);
          for (const key of Object.keys(bucket.responses)) {
            bucket.responses[key] = {
              ...bucket.responses[key],
              isComplete: true,
              error: diag,
            };
          }
          if (isStillActive()) {
            setActiveResponses({ ...bucket.responses });
          }
        }
      }

      // Finalize: save record to server and (if still active) move to history
      const finalResponses = bucket.responses;
      const providerResults: Record<
        string,
        { model: string; mode?: string; output?: string; error?: string; duration_ms?: number; options?: Record<string, unknown>; metadata?: Record<string, unknown>; usage?: Record<string, unknown> | null }
      > = {};
      for (const [pid, resp] of Object.entries(finalResponses)) {
        // Recovery handle only survives to the saved record when the
        // provider has no usable output yet. A completed provider's handle
        // is stale by save time and would just burn a reconciler lookup.
        const metadata =
          !resp.text && resp.backgroundJobId
            ? { background_job_id: resp.backgroundJobId }
            : undefined;
        providerResults[pid] = {
          model: resp.model || "",
          ...(resp.mode ? { mode: resp.mode } : {}),
          output: resp.text || undefined,
          error: resp.text ? undefined : resp.error,
          duration_ms: resp.durationMs,
          options: resp.options,
          usage: resp.usage,
          ...(metadata ? { metadata } : {}),
        };
      }

      // Temp chats (sid === "") behave like regular multi-turn chats but
      // never touch the server: each completed turn is archived into the
      // local chatHistory (no addRecord, no loadHistory). The next send
      // appends another record. Toggling temp OFF clears chatHistory.
      if (!sid) {
        if (isStillActive()) {
          const tempRecord: ChatRecord = {
            prompt,
            mode,
            responses: { ...bucket.responses },
            providerOrder: [...bucket.providerOrder],
            timestamp: initiatedAt,
            ...(attachmentNames && attachmentNames.length > 0
              ? { attachmentNames }
              : {}),
            ...(attachmentIdsList && attachmentIdsList.length > 0
              ? { attachmentIds: attachmentIdsList }
              : {}),
          };
          const nextHistory = [...chatHistoryRef.current, tempRecord];
          chatHistoryRef.current = nextHistory;
          setChatHistory(nextHistory);
          setActiveResponses({});
          setActiveProviderOrder([]);
          setIsStreaming(false);
        }
        bucket.isStreaming = false;
        cleanupBucket(sid);
        setActiveRecordId(null);
        return;
      }

      // Save record to server (blocking), then reload history to display it.
      // Single source of truth: the server record. No inline chatHistory append
      // that can race with loadHistory from session-switch effects.
      let recordSaveFailed = false;
      let savedRecordId: string | null = null;
      try {
        const saved = await addRecord(sid, {
          // Reuse the same id we sent in chatRequest.record_id so the
          // server's upgrade-on-conflict merge sees a matching record
          // and never creates a duplicate. If the server-side persist
          // already wrote the record, this archive POST is a no-op
          // (existing good output is never overwritten); if it didn't,
          // this POST creates it as usual.
          ...(bucket.recordId ? { id: bucket.recordId } : {}),
          prompt,
          mode,
          targets: providerIds,
          providers: providerResults,
          created_at: initiatedAt.toISOString(),
          ...(attachmentNames && attachmentNames.length > 0 ? { attachment_names: attachmentNames } : {}),
          ...(attachmentIdsList && attachmentIdsList.length > 0 ? { attachment_ids: attachmentIdsList } : {}),
        });
        savedRecordId = saved?.id ?? null;
      } catch (err) {
        // Surface the save failure to the caller so they can show a toast +
        // skip downstream operations (e.g. auto-judge). Previously this only
        // logged to console — so a silent save failure looked like a
        // successful send to the rest of the pipeline, and downstream
        // handlers like handleJudge would later complain "no responses to
        // judge" with no clue that the record was lost. iOS canonical: same
        // surface-the-error behavior.
        recordSaveFailed = true;
        console.error("Failed to save record:", err);
      }

      // Clear streaming UI state. AWAIT loadHistory so chatHistory has the
      // freshly-saved record before sendMessage's promise resolves — otherwise
      // composer-Judge's `await handleSendMessage; await handleJudge` chain
      // races: handleJudge sees an empty chatHistory and bails with
      // "No responses to judge".
      if (isStillActive()) {
        setActiveResponses({});
        setActiveProviderOrder([]);
        setIsStreaming(false);
        await loadHistory(sid);
      }

      // If this finalize is from a user-Stop AND the record saved cleanly,
      // arm the post-stop Discard affordance. Cleared by next sendMessage
      // start, session switch, or click of the Discard button itself.
      if (bucket.userStopped && savedRecordId && !recordSaveFailed) {
        setRecentlyStoppedRecord({ sessionId: sid, recordId: savedRecordId });
      }

      // Mark bucket no-longer-streaming + remove it from the Map. Keeping
      // it would cause the session-switch effect to incorrectly try to
      // restore stale in-flight state on the next visit. The record is
      // safely persisted server-side and loadHistory above already pulled
      // it into chatHistory.
      bucket.isStreaming = false;
      cleanupBucket(sid);
      setActiveRecordId(null);

      // If the record save failed, throw so the caller (handleSendMessage)
      // can show a toast and downstream handlers (handleSendAndJudge) skip
      // their next step. The throw escapes the silent-failure trap where
      // the chat appeared to succeed but no record was persisted.
      if (recordSaveFailed) {
        throw new Error("Record save failed — please retry");
      }
    },
    [modes]
  );

  // ---------------------------------------------------------------------------
  // Per-provider Retry (OQ-S6-a)
  // ---------------------------------------------------------------------------
  //
  // Invoked from ResponseCard's Retry affordance when a provider entry is in
  // terminal-error. Strategy (in order):
  //   1. If the record still carries a recovery handle (`streamId` /
  //      `backgroundJobId`), try the corresponding GET — the server may have
  //      finished the job after the client bailed.
  //   2. Otherwise, re-run just this one provider via POST /chat/stream with
  //      `providers: [just-the-one]` and the original prompt from the record.
  //   3. On recovered output, PATCH the record so the fix survives reloads
  //      and the retry handle is cleared.
  //
  // Stream buffer TTL on the server is 2 days. Background jobs persist to
  // disk. Either handle is always worth a shot before a fresh request.
  const retryProvider = useCallback(
    async (
      sid: string,
      recordIndex: number,
      providerRequest: ProviderRequest,
      conversationHistory?: Array<{ role: string; content: string }>
    ): Promise<void> => {
      const providerId = providerRequest.provider;

      // Snapshot the current record. Read from the ref (not state) so we
      // don't need chatHistory as a dep and always see the latest value.
      const history = chatHistoryRef.current;
      if (recordIndex >= history.length) {
        throw new Error("Record not found");
      }
      const rec = history[recordIndex];
      const serverRecordId = rec.serverRecordId;
      if (!serverRecordId) {
        throw new Error("Record has no serverRecordId");
      }
      const prior = rec.responses[providerId];

      // Mark the provider card as "retrying": clear the error so the copy
      // doesn't linger behind the spinner, flip `isComplete` to false so the
      // header spinner kicks in again. INV-3 still holds — we don't touch
      // `text`, and an empty text with no error is the same "pending" shape
      // used everywhere else.
      setChatHistory((prev) => {
        if (recordIndex >= prev.length) return prev;
        const cur = prev[recordIndex];
        const p = cur.responses[providerId];
        if (!p) return prev;
        const updated = [...prev];
        updated[recordIndex] = {
          ...cur,
          responses: {
            ...cur.responses,
            [providerId]: { ...p, text: "", error: undefined, isComplete: false, wasInterrupted: false },
          },
        };
        return updated;
      });

      // Helper: commit recovered output both locally and to the server.
      const commitRecovered = async (args: {
        text: string;
        model?: string;
        durationMs?: number;
        options?: Record<string, unknown>;
        usage?: Record<string, unknown> | null;
        meta?: string | null;
      }) => {
        setChatHistory((prev) => {
          if (recordIndex >= prev.length) return prev;
          const cur = prev[recordIndex];
          const p = cur.responses[providerId];
          if (!p) return prev;
          const updated = [...prev];
          updated[recordIndex] = {
            ...cur,
            responses: {
              ...cur.responses,
              [providerId]: {
                ...p,
                text: args.text,
                model: args.model || p.model,
                durationMs: args.durationMs ?? p.durationMs,
                options: args.options || p.options,
                usage: args.usage ?? p.usage,
                meta: args.meta ?? p.meta,
                isComplete: true,
                error: undefined,
                wasInterrupted: false,
                streamId: undefined,
                backgroundJobId: undefined,
              },
            },
          };
          return updated;
        });
        try {
          await patchRecord(sid, serverRecordId, {
            providers: {
              [providerId]: {
                model: args.model || prior?.model || "",
                output: args.text,
                duration_ms: args.durationMs,
                options: args.options,
                usage: args.usage,
                metadata: {},
              },
            },
          });
        } catch (patchErr) {
          console.error("retryProvider: PATCH failed", patchErr);
        }
      };

      // Helper: surface a retry failure back to terminal-error state.
      const flipBackToError = async (errMsg: string) => {
        setChatHistory((prev) => {
          if (recordIndex >= prev.length) return prev;
          const cur = prev[recordIndex];
          const p = cur.responses[providerId];
          if (!p || p.text) return prev; // INV-3
          const updated = [...prev];
          updated[recordIndex] = {
            ...cur,
            responses: {
              ...cur.responses,
              [providerId]: { ...p, isComplete: true, error: errMsg },
            },
          };
          return updated;
        });
      };

      // Step 1a: try the stream buffer if we have a streamId.
      if (prior?.streamId) {
        try {
          interface ResumeResponse { status: string; events: SSEEvent[]; stream_id: string }
          const data = (await resumeStream(prior.streamId)) as unknown as ResumeResponse | null;
          if (data && data.events && data.events.length > 0) {
            const replay = replayBufferedEvents(data.events, {
              [providerId]: {
                ...prior,
                text: "",
                error: undefined,
                isComplete: false,
                wasInterrupted: false,
              },
            });
            const replayed = replay.responses[providerId];
            // A partial buffered error is not recovery. Fall through to the
            // fresh request instead of re-delivering the failed answer.
            if (replayed?.text && !replayed.error) {
              await commitRecovered({
                text: replayed.text,
                model: replayed.model,
                durationMs: replayed.durationMs,
                options: replayed.options,
              });
              return;
            }
          }
        } catch (err) {
          console.error("retryProvider: resume stream failed", err);
        }
      }

      // Step 1b: try the background job store if we have a backgroundJobId.
      if (prior?.backgroundJobId) {
        try {
          const status = await pollBackgroundJob(prior.backgroundJobId);
          if (status.status === "done" && status.results) {
            const match = status.results.find((r) => r.provider === providerId);
            if (match && match.output) {
              await commitRecovered({
                text: match.output,
                model: match.model,
                durationMs: match.duration_ms,
                options: match.options,
                usage: match.usage,
                meta: match.meta,
              });
              return;
            }
          }
        } catch (err) {
          // 404 means gone — fall through to re-run. Transient errors also
          // fall through; if the re-run also fails, we'll flip back.
          console.log("retryProvider: background job not recoverable, re-running", err);
        }
      }

      // Step 2: fresh single-provider POST /chat/stream.
      // Pass the existing serverRecordId as record_id so the server's
      // _persist_stream_record path upgrades the existing record's empty/
      // errored provider slot in place (append_record merge logic) rather
      // than creating a duplicate record. commitRecovered's later
      // patchRecord overwrites the slot with the local-accumulator text
      // either way; if PATCH lands first, server-side persist's merge is
      // a no-op (existing good output is never overwritten).
      const chatRequest: ChatRequest = {
        prompt: rec.prompt,
        providers: [{ ...providerRequest, conversation_history: conversationHistory || [] }],
        mode: rec.mode,
        trace_id: crypto.randomUUID(),
        session_id: sid,
        record_id: serverRecordId,
        ...(rec.attachmentIds && rec.attachmentIds.length > 0 ? { attachment_ids: rec.attachmentIds } : {}),
      };

      let rerunText = "";
      let rerunModel: string | undefined;
      let rerunDuration: number | undefined;
      let rerunOptions: Record<string, unknown> | undefined;
      let rerunUsage: Record<string, unknown> | null | undefined;
      let rerunMeta: string | null | undefined;
      let rerunError: string | undefined;

      try {
        await streamChat(chatRequest, (event: SSEEvent) => {
          switch (event.type) {
            case "delta":
              if (event.provider === providerId) rerunText += event.delta;
              break;
            case "citations_replace":
              if (event.provider === providerId) rerunText = event.text;
              break;
            case "done":
              if (event.provider === providerId) {
                rerunModel = event.model;
                rerunDuration = event.duration_ms;
                rerunOptions = event.options;
                rerunUsage = event.usage;
                rerunMeta = event.meta;
              }
              break;
            case "error":
              if (event.provider === providerId) rerunError = event.error;
              break;
            default:
              break;
          }
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        await flipBackToError(`Retry failed: ${msg}`);
        throw err;
      }

      if (rerunText) {
        await commitRecovered({
          text: rerunText,
          model: rerunModel,
          durationMs: rerunDuration,
        options: rerunOptions,
        usage: rerunUsage,
        meta: rerunMeta,
        });
        return;
      }

      // Server closed the stream cleanly but never emitted text for this
      // provider — most likely an error event fired. Surface it.
      await flipBackToError(rerunError || "Retry produced no output");
    },
    []
  );

  const setRecordJudgeVerdict = useCallback(
    (recordIndex: number, verdict: JudgeResponse | null) => {
      setChatHistory((prev) => {
        if (recordIndex < 0 || recordIndex >= prev.length) return prev;
        const updated = [...prev];
        const old = updated[recordIndex];
        updated[recordIndex] = {
          ...old,
          ...(verdict ? { judgeVerdict: verdict } : { judgeVerdict: undefined }),
        };
        return updated;
      });
    },
    []
  );

  // Soft-delete a record from chatHistory. Reads chatHistoryRef (always-
  // fresh) to capture the record + its index synchronously, then
  // schedules the state update. Caller is expected to use the returned
  // {record, index} to wire an undo / commit flow.
  const removeRecord = useCallback(
    (recordId: string): { record: ChatRecord; index: number } | null => {
      const idx = chatHistoryRef.current.findIndex(
        (r) => r.serverRecordId === recordId
      );
      if (idx === -1) return null;
      const record = chatHistoryRef.current[idx];
      setChatHistory((prev) => {
        const next = [...prev];
        const i = next.findIndex((r) => r.serverRecordId === recordId);
        if (i !== -1) next.splice(i, 1);
        return next;
      });
      return { record, index: idx };
    },
    []
  );

  const restoreRecord = useCallback(
    (record: ChatRecord, index: number) => {
      setChatHistory((prev) => {
        const next = [...prev];
        const insertAt = Math.min(index, next.length);
        next.splice(insertAt, 0, record);
        return next;
      });
    },
    []
  );

  return {
    chatHistory,
    activeResponses,
    activeProviderOrder,
    isStreaming,
    isLoadingHistory,
    sendMessage,
    stopStreaming,
    recentlyStoppedRecord,
    stopJustPressed,
    discardRecentlyStopped,
    clearRecentlyStopped: () => {
      setRecentlyStoppedRecord(null);
      setStopJustPressed(false);
    },
    loadHistory,
    clearChat,
    retryProvider,
    setRecordJudgeVerdict,
    removeRecord,
    restoreRecord,
    chatHistoryRef,
    activeRecordId,
  };
}
